import { useCallback, useEffect, useState } from 'react';
import { useApp, useFetch, useMoney, usePrivateMoney } from '../store.tsx';
import { api, type BudgetProgress, type Dashboard, type Transaction } from '../api.ts';
import {
  PaceChart, Card, CategoryBar, DonutChart, DONUT_MIN_SHARE, Empty, ErrorState, PeriodPicker,
  SalaryQuestion, SectionTitle, Sheet, Skeleton, TransactionRow, type PeriodKey,
} from '../components.tsx';
import { formatDate, formatPct } from '../format.ts';
import { PullToRefresh } from '../PullToRefresh.tsx';
import { useWidgetSync } from '../useWidgetSync.ts';
import type { SafeToSpend } from '../api.ts';

/**
 * Home — the screen that must answer "how am I doing?" in under two seconds.
 * Headline figures first, one chart, then the breakdown. Nothing else.
 */
/**
 * What the headline is being compared against.
 *
 * The API always returns the immediately preceding period of the same length,
 * so the figure was already right — but the label said "vs last month" whatever
 * the period was, which reads as a mistake when the period is a single day.
 */
function comparisonLabel(period: PeriodKey, locale: string): string {
  const ar = locale === 'ar';
  switch (period) {
    case 'today': return ar ? 'مقارنة بالأمس' : 'vs yesterday';
    case 'week': return ar ? 'مقارنة بالأسبوع الماضي' : 'vs last week';
    case 'month': return ar ? 'مقارنة بالشهر الماضي' : 'vs last month';
    case 'last_month': return ar ? 'مقارنة بالشهر السابق له' : 'vs the month before';
    case 'year': return ar ? 'مقارنة بالعام الماضي' : 'vs last year';
    // The rolling windows compare with the window immediately before them.
    default: return ar ? 'مقارنة بالفترة السابقة' : 'vs the period before';
  }
}

export function HomeScreen({ onOpenTransaction, onSeeAll, onOpenCategory }: {
  onOpenTransaction: (id: string) => void;
  onSeeAll: () => void;
  onOpenCategory: (category: string) => void;
}): JSX.Element {
  const { t, locale, user, categoryMeta, categoryLabel, activeAccountId, setActiveAccountId } = useApp();
  const money = useMoney();
  const privateMoney = usePrivateMoney();
  // Part of the request path, so a change re-fetches rather than filtering
  // whatever happened to be cached.
  const accountQuery = activeAccountId ? `&accountId=${activeAccountId}` : '';
  const [period, setPeriod] = useState<PeriodKey>('today');

  const { data, error, loading, reload } = useFetch<Dashboard>(
    `/dashboard?period=${period}${accountQuery}`, [period, activeAccountId],
  );
  const { data: recent, reload: reloadRecent } = useFetch<{ transactions: Transaction[] }>(
    `/transactions?limit=5${activeAccountId ? `&accountId=${activeAccountId}` : ''}`,
    [period, activeAccountId],
  );
  const { data: accountList } = useFetch<{ accounts: { id: string; displayName: string }[] }>('/accounts');
  // The number people actually open the app for. Kept on Home rather than only
  // on Plan, because a figure you have to go looking for stops being consulted
  // before you spend — which is the only moment it can change anything.
  const { data: safeToSpend, reload: reloadSafeToSpend } = useFetch<SafeToSpend>('/safe-to-spend');
  const activeAccount = accountList?.accounts.find((a) => a.id === activeAccountId) ?? null;

  // Drop a filter that points at an account this user does not have.
  //
  // The selection is stored per device, not per account, and survives signing
  // out. Signing in as somebody else therefore filtered their dashboard to a
  // stranger's account id: every figure read zero, and the banner that explains
  // a filter could not render because the account was not in their list. A
  // finance app showing "you have spent nothing" with no explanation is the
  // worst possible way to be wrong.
  useEffect(() => {
    if (activeAccountId && accountList && !activeAccount) setActiveAccountId(null);
  }, [activeAccountId, accountList, activeAccount, setActiveAccountId]);
  const { data: alerts, reload: reloadAlerts } = useFetch<{
    alerts: Alert[];
  }>('/alerts');
  const [showAlerts, setShowAlerts] = useState(false);

  // Everything visible, not just the headline — a refresh that left the recent
  // list stale would be worse than none, because it would look answered.
  const refreshAll = useCallback(
    () => Promise.all([reload(), reloadRecent(), reloadAlerts()]),
    [reload, reloadRecent, reloadAlerts],
  );

  // Native only. Re-runs whenever the dashboard changes, so the widget is
  // refreshed by the same act that refreshed the screen.
  useWidgetSync(data);

  // The API labels periods in English. Localized labels live in the client
  // dictionary, so the UI uses those rather than echoing the server's.
  const PERIOD_LABELS: Record<string, string> = {
    today: t('today'), week: t('thisWeek'), month: t('thisMonth'),
    last_month: t('lastMonth'), last_30_days: t('last30'),
    // A key missing here falls through to the server's English label, so a
    // period added to the picker and forgotten here renders untranslated.
    last_90_days: t('last90'), last_6_months: t('last6Months'), year: t('thisYear'),
  };

  const currency = data?.currency ?? user?.currency ?? 'AED';

  return (
    <PullToRefresh onRefresh={refreshAll}>
    <div className="screen">
      <div className="row row--between" style={{ marginBottom: 14 }}>
        <div>
          <h1 style={{ fontSize: 21, fontWeight: 680, letterSpacing: '-0.02em' }}>
            {user?.name ? user.name : t('appName')}
          </h1>
        </div>
      </div>

      {/* Above the figure it produced, not below it: the number is wrong
          until this is answered, and a correction offered after the fact is
          one the reader has already acted on. */}
      {safeToSpend?.salaryQuestion && (
        <SalaryQuestion question={safeToSpend.salaryQuestion} currency={currency}
                        onAnswered={() => { void reloadSafeToSpend(); void reload(); }} />
      )}

      {safeToSpend && safeToSpend.basis !== 'insufficient_data' && (
        <Card style={{ marginBottom: 12 }}>
          <div className="row row--between" style={{ alignItems: 'flex-end' }}>
            <div>
              <div className="tiny muted" style={{ letterSpacing: '0.06em', fontWeight: 600 }}>
                {locale === 'ar' ? 'متاح للإنفاق' : 'SAFE TO SPEND'}
              </div>
              <div style={{
                fontSize: 27, fontWeight: 700, letterSpacing: '-0.03em',
                color: safeToSpend.availableMinor < 0 ? 'var(--negative)' : 'var(--text)',
              }}>
                {privateMoney(safeToSpend.availableMinor, currency)}
              </div>
            </div>
            <div style={{ textAlign: locale === 'ar' ? 'left' : 'right' }}>
              {/* "يوميًا" — per day. It read "اليوم", today, which is the same
                  ambiguity the Plan card had in English: a per-day budget
                  labelled as though it were a fact about today. */}
              <div className="tiny muted">{locale === 'ar' ? 'يوميًا' : 'per day'}</div>
              <div className="strong" style={{ fontSize: 16 }}>
                {privateMoney(safeToSpend.dailyAllowanceMinor, currency)}
              </div>
            </div>
          </div>
          <div className="tiny muted" style={{ marginTop: 6 }}>
            {safeToSpend.cycle.remainingDays === 0
              ? (locale === 'ar' ? 'آخر يوم في الدورة' : 'last day of this cycle')
              : (locale === 'ar'
                  ? `${safeToSpend.cycle.remainingDays} يوم متبقٍ${safeToSpend.cycle.anchored ? ' حتى الراتب' : ''}`
                  : `${safeToSpend.cycle.remainingDays} days left${safeToSpend.cycle.anchored ? ' until payday' : ''}`)}
          </div>
        </Card>
      )}

      <PeriodPicker value={period} onChange={setPeriod} />

      {loading && !data && <div style={{ marginTop: 16 }}><Skeleton height={150} count={1} /></div>}
      {error && <ErrorState message={error} onRetry={reload} />}

      {data && (
        <>
          <div className="hero" style={{ marginTop: 14 }}>
            <div className="hero__label">{t('spent')} · {PERIOD_LABELS[period] ?? data.period.label}</div>
            <div className="hero__amount">{money(data.summary.spentMinor, currency)}</div>

            {/* "This month" no longer means the calendar one — it runs payday to
                payday, so on the 16th it may have started on 27 July. A label
                whose meaning changed without saying so is how a correct figure
                gets read as a wrong one, and this is the screen where the two
                would be compared. Only for the month periods: a week needs no
                explaining. */}
            {(period === 'month' || period === 'last_month') && (
              <div className="tiny muted" style={{ marginTop: 2 }}>
                {formatDate(data.period.from, locale)} – {formatDate(data.period.to, locale)}
              </div>
            )}

            {data.previousSummary.spentMinor > 0 && (
              <ChangeLine
                current={data.summary.spentMinor}
                previous={data.previousSummary.spentMinor}
                label={comparisonLabel(period, locale)}
              />
            )}

            {/* The currency is already stated in the headline, so these three
                figures omit it — with it they wrap onto two lines on a phone. */}
            {/* These three adapt to the period rather than assuming a month.
                On a single day, income is almost always zero, which made
                "Remaining" a red negative mirror of the amount above it — an
                alarming figure that told the user nothing. Daily average is
                likewise the same number as the headline on a one-day period. */}
            <div className="hero__meta">
              <div className="hero__meta-item">
                <div className="hero__meta-label">{t('income')}</div>
                <div className="hero__meta-value money--pos">
                  {privateMoney(data.summary.incomeMinor, currency, { compact: true, bare: true })}
                </div>
              </div>

              {data.summary.incomeMinor > 0 ? (
                <div className="hero__meta-item">
                  <div className="hero__meta-label" title={t('remainingHelp')}>{t('remaining')}</div>
                  <div className="hero__meta-value" style={{
                    color: data.summary.remainingMinor >= 0 ? 'var(--text)' : 'var(--negative)',
                  }}>
                    {money(data.summary.remainingMinor, currency, { compact: true, bare: true })}
                  </div>
                </div>
              ) : (
                <div className="hero__meta-item">
                  <div className="hero__meta-label">{t('transactions')}</div>
                  <div className="hero__meta-value">{data.summary.transactionCount}</div>
                </div>
              )}

              {period !== 'today' && (
                <div className="hero__meta-item">
                  <div className="hero__meta-label">{t('dailyAverage')}</div>
                  <div className="hero__meta-value">
                    {money(data.summary.dailyAverageMinor, currency, { compact: true, bare: true })}
                  </div>
                </div>
              )}
            </div>

            {data.summary.transfersMinor > 0 && (
              <div className="tiny muted" style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
                {money(data.summary.transfersMinor, currency)} {locale === 'ar'
                  ? 'حُوّلت بين حساباتك ولم تُحتسب كإنفاق.'
                  : 'moved between your own accounts — not counted as spending.'}
              </div>
            )}
          </div>

          {/* An account filter that cannot be seen is the worst bug a finance
              app can have: every total is smaller and nothing says why. It is
              shown wherever it applies, and can be cleared from here. */}
          {activeAccount && (
            <button
              className="row row--between account-chip"
              onClick={() => setActiveAccountId(null)}
            >
              <span className="tiny strong">
                {locale === 'ar' ? `الحساب: ${activeAccount.displayName}` : `Showing ${activeAccount.displayName} only`}
              </span>
              <span className="tiny">{locale === 'ar' ? 'إظهار الكل ✕' : 'Show all ✕'}</span>
            </button>
          )}

          {/* Tappable, because the previous card was a dead end: it named a
              thing to deal with and offered no way to deal with it, so an alert
              once raised stayed on this screen for good. */}
          {alerts?.alerts?.length ? (
            <button className="card" style={{ marginTop: 12, borderColor: 'var(--warning)', width: '100%', textAlign: 'start' }}
                    onClick={() => setShowAlerts(true)}>
              <div className="row" style={{ gap: 10 }}>
                <span aria-hidden>🔔</span>
                <span className="grow small strong">
                  {(locale === 'ar' && alerts.alerts[0]!.titleAr) || alerts.alerts[0]!.title}
                </span>
                {alerts.alerts.length > 1 && (
                  <span className="badge badge--neutral" dir="ltr">+{alerts.alerts.length - 1}</span>
                )}
                <span className="muted" aria-hidden>›</span>
              </div>
            </button>
          ) : null}

          {/* Two points minimum: a cumulative line across a single day renders
              as an empty box with a legend under it. */}
          {data.dailyTrend.filter((d) => d.spentMinor > 0).length > 1 && (
            <Card style={{ marginTop: 12 }}>
              <PaceChart
                points={data.dailyTrend}
                previousPoints={data.previousDailyTrend}
                currency={currency}
                currentLabel={PERIOD_LABELS[period] ?? data.period.label}
              />
            </Card>
          )}

          <SectionTitle>{t('spendingBreakdown')}</SectionTitle>
          {data.byCategory.length === 0 ? (
            <Card><div className="muted small center" style={{ padding: 12 }}>{t('noSpendingYet')}</div></Card>
          ) : (
            <Card>
              <div className="row" style={{ justifyContent: 'center', marginBottom: 8 }}>
                <DonutChart
                  slices={data.byCategory.map((c) => ({
                    key: c.category,
                    value: c.totalMinor,
                    color: categoryMeta(c.category).color,
                    label: categoryLabel(c.category),
                  }))}
                  centerValue={money(data.summary.spentMinor, currency, { compact: true, bare: true })}
                  centerLabel={currency}
                />
              </div>
              <div>
                {(() => {
                  // Computed from the same array the chart was handed, not from
                  // the server's sharePct, so the two cannot disagree about what
                  // counts as small enough to collapse.
                  const donutTotal = data.byCategory.reduce((s, c) => s + c.totalMinor, 0);
                  return data.byCategory.slice(0, 7).map((c) => (
                    <CategoryBar
                      key={c.category}
                      categoryKey={c.category}
                      amountMinor={c.totalMinor}
                      sharePct={c.sharePct}
                      currency={currency}
                      swatchColor={donutTotal && c.totalMinor / donutTotal >= DONUT_MIN_SHARE
                        ? categoryMeta(c.category).color
                        : 'var(--border-strong)'}
                      onClick={() => onOpenCategory(c.category)}
                    />
                  ));
                })()}
              </div>
            </Card>
          )}

          {data.budgets.length > 0 && (
            <>
              <SectionTitle>{t('budgets')}</SectionTitle>
              <BudgetPace
                budgets={data.budgets}
                currency={currency}
                filteredToAccount={Boolean(activeAccountId)}
              />
            </>
          )}

          <SectionTitle action={
            <button className="btn btn--ghost btn--sm" onClick={onSeeAll}>{t('seeAll')} →</button>
          }>
            {t('recentActivity')}
          </SectionTitle>

          {recent?.transactions.length ? (
            <Card className="card--flush">
              {recent.transactions.map((tx) => (
                <TransactionRow key={tx.id} tx={tx} onClick={() => onOpenTransaction(tx.id)} />
              ))}
            </Card>
          ) : (
            <Empty icon="🌱" title={t('noTransactions')} body={t('noTransactionsBody')} />
          )}
        </>
      )}

      {showAlerts && (
        <AlertsSheet
          alerts={alerts?.alerts ?? []}
          onClose={() => setShowAlerts(false)}
          onChanged={reloadAlerts}
        />
      )}
    </div>
    </PullToRefresh>
  );
}

interface Alert {
  id: string; title: string; body: string;
  titleAr: string | null; bodyAr: string | null; severity: string;
}

/**
 * How each budget is doing — one row per budget, no total.
 *
 * This used to add every monthly limit together and show spend against the sum.
 * Asked what the figures meant, twice, and the honest answer was that the total
 * described nothing: with a 3,000 restaurants limit and a 3,000 fuel limit it
 * announced a budget of 6,000, which is not a cap on anything. Spending against
 * it counted only the categories that happen to have a limit — so it read
 * 1,116 beside a donut reading 4,003, and the gap had no explanation on screen.
 *
 * Worse, it divided the remainder by the days left and called it "a day to stay
 * inside": a daily allowance for restaurants and fuel, one screen away from a
 * safe-to-spend figure that means something entirely different.
 *
 * A budget is per category. Shown per category, every number explains itself
 * and there is nothing left to reconcile.
 */
function BudgetPace({ budgets, currency, filteredToAccount }: {
  budgets: BudgetProgress[];
  currency: string;
  filteredToAccount: boolean;
}): JSX.Element | null {
  const { locale, categoryLabel: categoryLabelOf } = useApp();
  const money = useMoney();
  const ar = locale === 'ar';

  // Monthly budgets only. A weekly and a yearly limit in the same list are
  // measured over different windows, and the rows would not be comparable.
  const monthly = budgets.filter((b) => b.period === 'monthly');
  if (!monthly.length) return null;

  // Server sorts by progress, highest first — the ones nearest their limit are
  // the ones worth the space. Three, because this is a glance on a screen that
  // already carries a chart and a category list; the full set lives on Goals.
  const shown = monthly.slice(0, 3);
  const rest = monthly.length - shown.length;
  const first = monthly[0]!;

  return (
    <Card>
      <div className="row row--between" style={{ gap: 12, marginBottom: 12 }}>
        <span className="tiny muted">
          {formatDate(first.windowFrom, locale)} – {formatDate(first.windowTo, locale)}
        </span>
        <span className="tiny muted">
          {first.daysRemaining} {ar ? 'يوم متبقٍ' : first.daysRemaining === 1 ? 'day left' : 'days left'}
        </span>
      </div>

      <div className="stack" style={{ gap: 14 }}>
        {shown.map((b) => {
          const pct = Math.min(100, b.progressPct);
          /*
           * Colour says how the budget is doing, not which category it is.
           *
           * Category hues belong to the breakdown directly above, where they
           * tie a row to its slice of the bar. Repeating them here made four
           * unrelated colours compete a few pixels below that legend and gave
           * a healthy budget the same visual weight as a blown one. On a
           * progress bar the only thing worth colouring is the trouble.
           */
          const colour = b.status === 'exceeded' ? 'var(--negative)'
            : b.status === 'warning' ? 'var(--warning)'
            : 'var(--accent)';
          return (
            <div key={b.id}>
              <div className="row row--between" style={{ gap: 10, marginBottom: 5 }}>
                <span className="small strong">{categoryLabelOf(b.category)}</span>
                {/* Spent and limit together on the row they belong to. This is
                    the whole fix: "847 of 3,000" beside the word Restaurants
                    cannot be read as anything else. */}
                <span className="small muted money">
                  {money(b.spentMinor, currency, { compact: true })}
                  {' '}{ar ? 'من' : 'of'}{' '}
                  {money(b.budgetMinor, currency, { compact: true })}
                </span>
              </div>
              <div className="bar">
                <span className="bar__fill" style={{ width: `${pct}%`, background: colour }} />
              </div>
            </div>
          );
        })}
      </div>

      {rest > 0 && (
        <div className="tiny muted" style={{ marginTop: 12 }}>
          {ar ? `و${rest} ميزانية أخرى في الأهداف` : `${rest} more in Goals`}
        </div>
      )}

      {/* Budgets deliberately span every account: a restaurants limit is about
          restaurant spending, not about which card paid. Saying so matters most
          when the rest of the screen is showing one account. */}
      {filteredToAccount && (
        <div className="tiny muted" style={{ marginTop: 6, lineHeight: 1.5 }}>
          {ar
            ? 'الميزانيات تشمل كل الحسابات، وليست الحساب المعروض فقط.'
            : 'Budgets cover every account, not just the one shown above.'}
        </div>
      )}
    </Card>
  );
}

/**
 * The alerts themselves, and a way to be rid of them.
 *
 * Most alert types describe a pattern rather than a task — "spending trending
 * up" is not something that can be completed — so without a dismissal they
 * accumulate with no way out. The ones that *are* tasks clear themselves once
 * the task is done; this is for the rest.
 */
function AlertsSheet({ alerts, onClose, onChanged }: {
  alerts: Alert[];
  onClose: () => void;
  onChanged: () => void;
}): JSX.Element {
  const { t, locale, toastError } = useApp();
  const ar = locale === 'ar';
  const [busy, setBusy] = useState(false);

  const dismiss = async (id: string) => {
    setBusy(true);
    try {
      await api.post(`/alerts/${id}/dismiss`);
      onChanged();
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  const dismissAll = async () => {
    setBusy(true);
    try {
      await Promise.all(alerts.map((a) => api.post(`/alerts/${a.id}/dismiss`)));
      onChanged();
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet title={t('alerts')} onClose={onClose}>
      <div className="stack">
        {!alerts.length && <p className="small muted">{t('noAlerts')}</p>}

        {alerts.map((a) => (
          <Card key={a.id}>
            <div className="row row--between" style={{ gap: 10, alignItems: 'flex-start' }}>
              <span className="grow">
                <span className="small strong" style={{ display: 'block' }}>
                  {(ar && a.titleAr) || a.title}
                </span>
                <span className="tiny muted" style={{ display: 'block', marginTop: 4, lineHeight: 1.55 }}>
                  {(ar && a.bodyAr) || a.body}
                </span>
              </span>
              <button className="icon-btn icon-btn--danger" aria-label={`${t('dismiss')} ${a.title}`}
                      disabled={busy} onClick={() => dismiss(a.id)}>✕</button>
            </div>
          </Card>
        ))}

        {alerts.length > 1 && (
          <button className="btn btn--secondary btn--block" onClick={dismissAll} disabled={busy}>
            {ar ? 'تجاهل الكل' : 'Dismiss all'}
          </button>
        )}
      </div>
    </Sheet>
  );
}

/** Month-over-month delta line under the headline figure. */
function ChangeLine({ current, previous, label }: { current: number; previous: number; label: string }): JSX.Element | null {
  if (!previous) return null;
  const pct = ((current - previous) / previous) * 100;
  if (!Number.isFinite(pct)) return null;
  const up = pct > 0;
  return (
    <div className="tiny" style={{ color: 'var(--text-secondary)', marginTop: 2 }}>
      <span style={{ color: up ? 'var(--negative)' : 'var(--positive)', fontWeight: 620 }}>
        {up ? '↑' : '↓'} {formatPct(Math.abs(pct))}
      </span>{' '}
      {label}
    </div>
  );
}
